<template>
  <div ref="modal" :id="id" class="nr-modal" aria-hidden="true" v-if="show">
    <div
      class="nr-modal__overlay"
      tabindex="-1"
      @click="handleOverlayClick"
      ref="overlay"
    >
      <div
        role="dialog"
        aria-modal="true"
        :aria-label="title"
        :class="modalClassnames"
      >
        <div
          :class="[
            'nr-modal__header',
            {
              'nr-modal__header--hidden-footer':
                !showFooter && type !== 'warning'
            },
            { 'nr-modal__header-warning': type === 'warning' }
          ]"
        >
          <slot name="header">
            <h2
              :class="[
                'nr-modal__title',
                {
                  'nr-modal__title--hidden-footer': !showFooter
                }
              ]"
            >
              {{ title }}
            </h2>
          </slot>
        </div>
        <div
          :class="modalContentClassnames"
          :id="`${id}__content`"
          ref="modal-content"
          tabindex="-1"
        >
          <slot>Modal content goes here</slot>
        </div>
        <div v-if="showFooter" class="nr-modal__footer">
          <NrButton
            v-if="hasSecondaryButton"
            @click="handleSecondaryClick"
            variant="bordered-primary"
            :aria-label="secondaryButtonText"
            >{{ secondaryButtonText }}</NrButton
          >
          <NrButton
            @click="handlePrimaryClick"
            :aria-label="primaryButtonText"
            :disabled="disableButtons"
            >{{ primaryButtonText }}</NrButton
          >
        </div>
        <NrIconButton
          v-if="showClose"
          class="nr-modal__close"
          variant="minimal-neutral"
          size="small"
          :icon="{ name: 'times', type: 'regular' }"
          aria-label="Close modal"
          @click="handleCloseClick"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { PropType, defineComponent } from 'vue'
import { uniqueId, debounce } from 'lodash'
import Micromodal from 'micromodal'
import NrButton from '../NrButton/nr-button.vue'
import NrIconButton from '../NrIconButton/nr-icon-button.vue'

type ModalSize = 'small' | 'medium' | 'large'
type ModalTypes = 'default' | 'warning'

interface Data {
  id: string
  showScrollbarMarker: boolean
  [key: string]: unknown
}

export default defineComponent({
  name: 'NrModal',
  components: { NrButton, NrIconButton },
  props: {
    size: {
      type: String as PropType<ModalSize>,
      default: 'medium'
    },
    show: {
      type: Boolean,
      default: false
    },
    showClose: {
      type: Boolean,
      default: false
    },
    showFooter: {
      type: Boolean,
      default: true
    },
    closeOnEsc: {
      type: Boolean,
      default: true
    },
    closeOnOverlayClick: {
      type: Boolean,
      default: true
    },
    hasSecondaryButton: {
      type: Boolean,
      default: false
    },
    primaryButtonText: {
      type: String,
      default: 'Close'
    },
    secondaryButtonText: {
      type: String,
      default: 'Cancel'
    },
    disableButtons: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: 'Modal title'
    },
    type: {
      type: String as PropType<ModalTypes>,
      default: 'default'
    }
  },
  data(): Data {
    return {
      id: uniqueId('nr-modal-'),
      showScrollbarMarker: false
    }
  },
  computed: {
    modalContentClassnames(): { [key: string]: boolean } {
      return {
        'nr-modal__content': true,
        'nr-modal__content--scrollbar': this.showScrollbarMarker
      }
    },
    modalClassnames(): { [key: string]: boolean } {
      return {
        'nr-modal__container': true,
        [`nr-modal__container--${this.size}`]: true
      }
    }
  },
  created() {
    this.onResize = debounce(this.onResize, 400)
  },
  mounted() {
    this.show && this.toggleModal(this.show)
    window.addEventListener('resize', this.onResize)
  },
  beforeUnmount() {
    // Normally `toggleModal` is called when `show` changes, but if this
    // component is unmounted while `show` is true we need to call it here.
    // Otherwise, `Micromodal.close` is never called.
    if (this.show) {
      this.toggleModal(false)
    }
    window.removeEventListener('resize', this.onResize)
    document.removeEventListener('keydown', this.disableCloseOnEsc)
  },
  methods: {
    onResize() {
      const element = this.$refs['modal-content'] as HTMLElement | undefined

      if (!element) {
        return
      }

      this.showScrollbarMarker = element.scrollHeight > element.clientHeight
    },
    toggleModal(state: boolean) {
      if (state) {
        this.$nextTick(() => {
          document.addEventListener('keydown', this.disableCloseOnEsc)
          Micromodal.show(this.id, {
            openClass: 'nr-modal--open',
            disableScroll: true,
            disableFocus: true,
            onClose: () => this.$emit('on-close-click')
          })
        })
      } else {
        Micromodal.close(this.id)
        document.removeEventListener('keydown', this.disableCloseOnEsc)
      }
    },
    disableCloseOnEsc(event?: KeyboardEvent) {
      if (this.closeOnEsc) {
        return
      }

      if (/Esc/.test(event?.key || '')) {
        event?.stopImmediatePropagation()
      }
    },
    handlePrimaryClick(event?: MouseEvent) {
      this.$emit('on-primary-click', event)
    },
    handleSecondaryClick(event?: MouseEvent) {
      this.$emit('on-secondary-click', event)
    },
    handleCloseClick(event?: Event) {
      this.$emit('on-close-click', event)
    },
    handleOverlayClick(event: MouseEvent) {
      if (this.closeOnOverlayClick && this.$refs.overlay === event.target) {
        this.handleCloseClick(event)
      }
    }
  },
  watch: {
    show(next) {
      this.toggleModal(next)
    }
  }
})
</script>

<style lang="scss">
@import '../../styles/colors.scss';
@import '../../styles/tokens/tokens.scss';

.nr-modal {
  display: none;
}

.nr-modal--open {
  display: block;
}

.nr-modal__overlay {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 50;
  background-color: rgba(9, 9, 11, 0.86);
}

.nr-modal__container {
  display: flex;
  flex-direction: column;
  max-height: 100%;
  max-width: 100%;
  width: 100%;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate3d(-50%, -50%, 0);
  background-color: white;

  @media screen and (min-width: 27em) {
    max-height: calc(100vh - 3em);
    min-height: 20em;
    width: calc(100% - 3em);
    border-radius: 0.375em;
  }
}

.nr-modal__container--small {
  @media screen and (min-width: 27em) {
    max-width: 24em;
  }
}

.nr-modal__container--medium {
  @media screen and (min-width: 27em) {
    max-width: 40em;
  }
}

.nr-modal__container--large {
  @media screen and (min-width: 27em) {
    max-width: 64em;
  }
}

.nr-modal__header {
  flex: 0 0 auto;
  padding: 1.25em;
  background-color: #f4f4f6;

  @media screen and (min-width: 27em) {
    border-radius: 0.375em 0.375em 0 0;
  }
}

.nr-modal__header-warning {
  background-color: $color-red-100;
  .nr-modal__title {
    color: $color-white;
  }
}

.nr-modal__header--hidden-footer {
  background-color: $white;
  padding: 1em;
}

.nr-modal__title {
  color: $grey-darker;
  font-size: 1.5em;
}

.nr-modal__title--hidden-footer {
  max-width: 94%;
  font-size: 1.25em;
}

.nr-modal__content {
  flex: 1 1 auto;
  padding: 1.25em;
  overflow: auto;
}

.nr-modal__content--scrollbar {
  border-bottom: 1px solid #eeeeee;
}

.nr-modal__footer {
  flex: 0 0 auto;
  padding: 1.25em;
  text-align: right;

  button {
    margin-right: 0.75em;

    &:last-child {
      margin-right: 0;
    }
  }
}

.nr-modal__close {
  position: absolute;
  top: 0.5em;
  right: 0.5em;
}
</style>
